import { useCallback, useEffect, useMemo } from 'react';

import { AccessLevel, ActivationStatus, Member, UserStatus } from '@/entities/vfo/types';
import { useNotices } from '@/features/notices/useNotices';
import { useApi } from '@/shared/api/hooks/useApi';
import { useAuthStore } from '@/shared/store/auth.store';

import { sortMembers } from '../helpers';
import { useCasesStore } from '../stores/cases.store';
import { useMembersStore } from '../stores/members.store';

import { useIsFounder } from './useIsFounder';

export const useMembers = () => {
  const api = useApi();
  const notices = useNotices();
  const {
    members: allMembers,
    isLoading,
    setMembers,
    removeMember: removeMemberFromStore,
    setLoading,
    setInitialized,
  } = useMembersStore();
  const { cases, isInitialized: isCasesInitializedGlobal } = useCasesStore();
  const { account, tenantOwners } = useAuthStore();
  const isFounder = useIsFounder();

  const fetchMembers = useCallback(async () => {
    if (isLoading) return undefined;
    setLoading(true);
    const [caseMembersResult, invitedMembersResult, scopesResult] = await Promise.allSettled([
      api.casesGetMembers(),
      isFounder ? api.scopesInvitations() : null,
      isFounder ? api.scopesGetAll() : null,
    ]);

    const caseMembers = caseMembersResult.status === 'fulfilled' ? caseMembersResult.value : null;
    const invitedMembers =
      invitedMembersResult.status === 'fulfilled' ? invitedMembersResult.value : null;
    const scopes = scopesResult.status === 'fulfilled' ? scopesResult.value : null;

    if (
      (caseMembers === null || caseMembers.error) &&
      (invitedMembers === null || invitedMembers.error) &&
      (scopes === null || scopes.error)
    ) {
      notices.error('Failed to get members');
      setLoading(false);
      setInitialized(true);
      return [];
    }

    const newMembers: Member[] = [];
    const emailSet = new Set<string>();

    const me = await api.authMe();
    if (me.data) {
      newMembers.push({
        createdAt: me.data.createdAt,
        firstName: me.data?.firstName ?? undefined,
        lastName: me.data?.lastName ?? undefined,
        email: me.data?.email,
        profileId: account?.id,
        status: UserStatus.OFFLINE,
        lastActiveDate: undefined,
        isFounder: false,
        activationStatus: ActivationStatus.ACTIVE,
      });
    }

    if (!isFounder) {
      const tenants = await api.scopeControllerFindTenantProfiles();
      if (tenants.data && tenants.data[0]) {
        if (newMembers[0] && tenants.data[0].tag) {
          newMembers[0].tag = tenants.data[0].tag;
        }

        tenants.data.forEach((item) => {
          if (!item.tag) return;
          newMembers.push({
            createdAt: tenants.data?.[0].createdAt,
            firstName: item.firstName ?? undefined,
            lastName: item.lastName ?? undefined,
            email: item.email,
            profileId: item.profileId,
            tag: item.tag,
            status: UserStatus.OFFLINE,
            lastActiveDate: undefined,
            isFounder: true,
            activationStatus: ActivationStatus.ACTIVE,
          });
        });
      }
    }

    // Process invited members
    if (invitedMembers && !invitedMembers.error) {
      invitedMembers.data?.forEach((item) => {
        if (item.scope === 'VFO' && !emailSet.has(item.email)) {
          newMembers.push({
            createdAt: item.createdAt,
            firstName: item.firstName ?? undefined,
            lastName: item.lastName ?? undefined,
            email: item.email,
            tag: item.tag,
            status: UserStatus.OFFLINE,
            lastActiveDate: undefined,
            activationStatus: ActivationStatus.INVITED,
            isFounder: false,
            invitationId: item.id,
          });
          emailSet.add(item.email);
        }
      });
    }

    // Process members with activated account but not in cases
    if (scopes && !scopes.error) {
      scopes.data?.forEach((item) => {
        if (item.scopeType !== 'VFO') return;

        const index = newMembers.findIndex((m) => m.email === item.email);

        if (index !== -1) {
          newMembers[index] = {
            ...newMembers[index],
            profileId: item.profileId,
            firstName: item.firstName ?? newMembers[index].firstName ?? undefined,
            lastName: item.lastName ?? newMembers[index].lastName ?? undefined,
            email: item.email,
            status: UserStatus.OFFLINE,
            lastActiveDate: undefined,
            isFounder: false,
            scopeId: item.id,
            activationStatus: ActivationStatus.INVITED,
          };
        } else {
          // family members
          newMembers.push({
            profileId: item.profileId,
            firstName: item.firstName ?? undefined,
            lastName: item.lastName ?? undefined,
            email: item.email,
            status: UserStatus.OFFLINE,
            lastActiveDate: undefined,
            isFounder: false,
            scopeId: item.id,
            activationStatus: ActivationStatus.INVITED,
          });
        }
      });
    }
    // Process case members
    const getStatus = (profileId: number) => {
      const member = useMembersStore.getState().members.find((m) => m.profileId === profileId);
      if (!member) return UserStatus.OFFLINE;
      return member.status;
    };

    if (caseMembers && !caseMembers.error) {
      caseMembers.data?.forEach((item) => {
        const index = newMembers.findIndex((m) => m.email === item.email);

        const isTenant = tenantOwners?.some(
          (t) => t.scopes.includes('VFO') && t.profileId === newMembers[index]?.profileId,
        );
        const isCurrentUserFounder = account?.id === newMembers[index]?.profileId && isFounder;

        const isMemberFounder = isTenant || isCurrentUserFounder;

        const data = {
          profileId: item.profileId,
          createdAt: item.registrationDate ?? item.createdAt,
          firstName: item.firstName ?? undefined,
          lastName: item.lastName ?? undefined,
          country: item.countryCode,
          email: item.email,
          status: getStatus(item.profileId),
          lastActiveDate: undefined,
          isFounder: isMemberFounder,
          activationStatus:
            isMemberFounder || item.status === 'REGISTERED'
              ? ActivationStatus.ACTIVE
              : ActivationStatus.PENDING,
          accessLevel: item.accessLevel as AccessLevel, // TODO: wait until  backend types fix
        };

        if (index !== -1) {
          newMembers[index] = {
            ...newMembers[index],
            ...data,
            country: item.countryCode ?? newMembers[index].country ?? undefined,
            tag: item.tagCode ?? newMembers[index].tag ?? undefined,
            firstName: item.firstName ?? newMembers[index].firstName ?? undefined,
            lastName: item.lastName ?? newMembers[index].lastName ?? undefined,
          };
        } else {
          newMembers.push({
            ...data,
            country: item.countryCode ?? undefined,
            tag: item.tagCode ?? undefined,
          });
        }
      });
    }

    const sortedMembers = sortMembers(newMembers);

    setMembers(sortedMembers);
    setLoading(false);
    setInitialized(true);
    return sortedMembers;
  }, [api, notices, cases, setMembers, setLoading, setInitialized]);

  const deleteMember = useCallback(
    async (profileId: number): Promise<boolean> => {
      const member = allMembers.find((m) => m.profileId === profileId);
      if (!member || !member?.scopeId) {
        notices.error('Failed to delete member');
        return false;
      }
      const [scopeResult, invitationResult] = await Promise.allSettled([
        api.scopesDelete(member.scopeId),
        member.invitationId ? api.vfoInvitationDelete(member.invitationId) : Promise.resolve(),
      ]);

      const response = scopeResult.status === 'fulfilled' ? scopeResult.value : undefined;
      const invitationResponse =
        invitationResult.status === 'fulfilled' ? invitationResult.value : undefined;

      if (response?.error || invitationResponse?.error) {
        notices.error('Failed to delete member');
        return false;
      }

      const memberToRemove = allMembers.find(
        (m) => m.scopeId === member.scopeId || m.invitationId === member.invitationId,
      );
      if (memberToRemove?.profileId) {
        removeMemberFromStore(memberToRemove.profileId);
      }
      return true;
    },
    [api, notices, allMembers, cases, removeMemberFromStore, fetchMembers],
  );

  const getMembersByCaseId = useCallback(
    (caseId: string): Member[] => {
      const caseMembers = cases.find((c) => c.id === caseId)?.members;
      return allMembers.filter(
        (item) => caseMembers?.some((caseMember) => caseMember.profileId === item.profileId),
      );
    },
    [allMembers, cases],
  );

  const membersWithoutCurrentUser = useMemo(() => {
    return allMembers.filter((item) => item.profileId !== account?.id);
  }, [allMembers, account]);

  const getByProfileId = useCallback(
    (profileId: number): Member | undefined => {
      return allMembers.find((m) => m.profileId === profileId);
    },
    [allMembers],
  );

  const currentUser = useMemo(() => {
    return allMembers.find((item) => item.profileId === account?.id);
  }, [allMembers, account]);

  const founder = useMemo(() => {
    return allMembers.find((item) => item.isFounder);
  }, [allMembers]);

  useEffect(() => {
    // get the live store state to avoid race conditions
    const { isInitialized, isLoading: isLoadingLive } = useMembersStore.getState();
    const { isInitialized: isCasesInitialized } = useCasesStore.getState();
    if (isInitialized || isLoadingLive || !isCasesInitialized) return;
    fetchMembers();
  }, [fetchMembers, isCasesInitializedGlobal]);

  return useMemo(
    () => ({
      currentUser,
      members: allMembers,
      membersWithoutCurrentUser,
      isLoading,
      getMembersByCaseId,
      fetchMembers,
      deleteMember,
      getByProfileId,
      founder,
    }),
    [
      currentUser,
      allMembers,
      membersWithoutCurrentUser,
      isLoading,
      getMembersByCaseId,
      fetchMembers,
      deleteMember,
      getByProfileId,
      founder,
    ],
  );
};
